import { latestResult } from "./algorithm.js";
import { LETTER_COMBOS_LONGEST_FIRST } from "./teaching-hints.js";
import { Profile, ReadingText } from "./type.app.js";

export const LESSON_INTRO_VIEWS = 3;

export type LessonSpan = {
  start: number;
  end: number;
};

export type LessonBeat = {
  spans: LessonSpan[];
  flashes: number;
};

export type LessonAnimation = {
  id: string;
  beats: LessonBeat[];
};

const FUNCTION_WORDS = new Set([
  "the",
  "a",
  "an",
  "to",
  "of",
  "is",
  "as",
  "at",
  "it",
  "in",
  "on",
  "or",
  "and",
  "for",
  "be",
]);

const MAGIC_E_EXCEPTIONS = new Set([
  "have",
  "give",
  "live",
  "love",
  "come",
  "some",
  "done",
  "none",
  "gone",
  "one",
  "were",
  "there",
  "where",
  "are",
  "else",
  "office",
]);

const OW_DIPHTHONG = new Set([
  "now",
  "how",
  "cow",
  "wow",
  "bow",
  "down",
  "brown",
  "town",
  "clown",
  "crowd",
  "owl",
  "howl",
  "fowl",
  "frown",
  "gown",
  "power",
  "tower",
  "flower",
  "shower",
]);

const OO_SHORT = new Set(["book", "look", "cook", "took", "good", "wood", "foot", "hook", "stood"]);

const EA_SHORT = new Set(["bread", "head", "dead", "ready", "heavy", "instead"]);

const VOWEL_TEAMS = new Set([
  "ai",
  "ay",
  "ee",
  "ea",
  "oa",
  "ow",
  "oe",
  "ie",
  "igh",
  "ue",
  "ew",
  "oo",
  "ou",
  "oi",
  "oy",
  "au",
  "aw",
  "ui",
  "ey",
  "eigh",
]);

const R_CONTROLLED = new Set(["ar", "or", "er", "ir", "ur", "air", "ear", "ore", "ure", "oor"]);

const DIGRAPHS = new Set(["qu", "ck", "ff", "ll", "ss", "zz", "sh", "ch", "th", "wh", "ph", "ng", "nk", "tch"]);

const isVowel = (letter: string, index: number): boolean => {
  const lower = letter.toLowerCase();
  if ("aeiou".includes(lower)) return true;
  return lower === "y" && index > 0;
};

type Candidate = {
  id: string;
  score: number;
  start: number;
  beats: LessonBeat[];
};

const comboScore = (combo: string): number => {
  if (combo.length >= 3) return 80;
  if (VOWEL_TEAMS.has(combo) || R_CONTROLLED.has(combo)) return 70;
  if (DIGRAPHS.has(combo)) return 40;
  return 20;
};

const doubleFlash = (start: number, end: number): LessonBeat => ({
  spans: [{ start, end }],
  flashes: 2,
});

const wordsIn = (text: string): { word: string; start: number }[] => {
  const words: { word: string; start: number }[] = [];
  const matcher = /[A-Za-z']+/g;
  let match: RegExpExecArray | null = matcher.exec(text);
  while (match) {
    const raw = match[0];
    const word = raw.replace(/^'+|'+$/g, "").toLowerCase();
    const lead = raw.match(/^'+/)?.[0].length ?? 0;
    if (word) words.push({ word, start: match.index + lead });
    match = matcher.exec(text);
  }
  return words;
};

const magicEInWord = (word: string): { vowel: number; e: number } | null => {
  if (word.length < 3 || !word.endsWith("e") || word.endsWith("ee")) return null;
  if (MAGIC_E_EXCEPTIONS.has(word)) return null;
  if (word.endsWith("ore") || word.endsWith("ure")) return null;
  const cons = word.length - 2;
  const consLetter = word[cons] ?? "";
  if (isVowel(consLetter, cons)) return null;
  const vowel = cons - 1;
  const vowelLetter = word[vowel] ?? "";
  if (vowel < 0 || !isVowel(vowelLetter, vowel)) return null;
  if (vowel > 0 && isVowel(word[vowel - 1] ?? "", vowel - 1)) return null;
  return { vowel, e: word.length - 1 };
};

const candidatesForWord = (word: string, offset: number): Candidate[] => {
  const found: Candidate[] = [];
  if (FUNCTION_WORDS.has(word)) return found;

  if (word === "you" || word.startsWith("you")) {
    const ou = word.indexOf("ou");
    if (ou >= 0) {
      found.push({
        id: "ou-you",
        score: 85,
        start: offset + ou,
        beats: [doubleFlash(offset + ou, offset + ou + 2)],
      });
    }
  }

  const owAt = word.indexOf("ow");
  if (owAt >= 0) {
    if (OW_DIPHTHONG.has(word)) {
      found.push({
        id: "ow-diphthong",
        score: 85,
        start: offset + owAt,
        beats: [doubleFlash(offset + owAt, offset + owAt + 2)],
      });
    } else if (word.endsWith("ow") || word === "owen" || word.endsWith("own")) {
      found.push({
        id: "ow-long",
        score: 85,
        start: offset + owAt,
        beats: [doubleFlash(offset + owAt, offset + owAt + 2)],
      });
    }
  }

  if (OO_SHORT.has(word)) {
    const oo = word.indexOf("oo");
    if (oo >= 0) {
      found.push({
        id: "oo-short",
        score: 85,
        start: offset + oo,
        beats: [doubleFlash(offset + oo, offset + oo + 2)],
      });
    }
  }

  if (EA_SHORT.has(word)) {
    const ea = word.indexOf("ea");
    if (ea >= 0) {
      found.push({
        id: "ea-short",
        score: 85,
        start: offset + ea,
        beats: [doubleFlash(offset + ea, offset + ea + 2)],
      });
    }
  }

  const magic = magicEInWord(word);
  if (magic) {
    found.push({
      id: "magic-e",
      score: 90,
      start: offset + magic.e,
      beats: [
        doubleFlash(offset + magic.e, offset + magic.e + 1),
        doubleFlash(offset + magic.vowel, offset + magic.vowel + 1),
      ],
    });
  }

  for (let index = 0; index < word.length; index += 1) {
    for (const combo of LETTER_COMBOS_LONGEST_FIRST) {
      if (!word.startsWith(combo, index)) continue;
      found.push({
        id: combo,
        score: comboScore(combo),
        start: offset + index,
        beats: [doubleFlash(offset + index, offset + index + combo.length)],
      });
      break;
    }
  }

  return found;
};

export const lessonAnimationFor = (text: ReadingText): LessonAnimation | null => {
  const raw = text.text;
  if (!raw.trim()) return null;
  const lower = raw.trim().toLowerCase();
  if (text.kind === "letter" || LETTER_COMBOS_LONGEST_FIRST.includes(lower)) {
    return null;
  }

  const candidates: Candidate[] = [];
  for (const { word, start } of wordsIn(raw)) {
    candidates.push(...candidatesForWord(word, start));
  }
  if (candidates.length === 0) return null;
  candidates.sort((left, right) => right.score - left.score || left.start - right.start);
  const best = candidates[0];
  if (!best) return null;
  return { id: best.id, beats: best.beats };
};

export const lessonPlayCount = (profile: Profile, textId: string): 0 | 1 | 2 => {
  const stat = profile.textStats[textId];
  const seen = stat ? stat.correct + stat.wrong + stat.skip + stat.wayTooEasy : 0;
  if (seen < LESSON_INTRO_VIEWS) return 1;
  if (latestResult(profile, textId) === "wrong") return 2;
  return 0;
};

export const lessonLetterIndexes = (animation: LessonAnimation): Set<number> => {
  const indexes = new Set<number>();
  for (const beat of animation.beats) {
    for (const span of beat.spans) {
      for (let index = span.start; index < span.end; index += 1) {
        indexes.add(index);
      }
    }
  }
  return indexes;
};
